// IM-UX-188 (IM-SAAS-004): recuperação de acesso. "Esqueci minha senha" pede o link (a resposta é sempre a mesma:
// a tela não revela se o e-mail tem conta); "Redefinir senha" lê o token do fragmento da URL (#token=…, que não vai para
// o servidor nem para os logs de acesso), define a senha nova e volta para a tela de acesso.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { BrandLogo } from '../components/BrandLogo';
import { useToast } from '../components/Toast';
import { accessRecoveryAdapter } from '../data/context';
import { friendlyError } from '../lib/errors';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { setError('Informe um e-mail válido.'); return; }
    setBusy(true); setError(null);
    try { await accessRecoveryAdapter.request(v); setSent(true); } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? 'Muitas tentativas. Aguarde um minuto e tente novamente.' : friendlyError(err, 'Não foi possível enviar o pedido agora.'));
    } finally { setBusy(false); }
  };
  return (
    <main className="onboarding">
      <BrandLogo variant="public" />
      <section className="card source-form" aria-labelledby="recover-title">
        <h1 id="recover-title" className="card-title">Recuperar acesso</h1>
        {sent ? (
          <div role="status">
            <p className="card-sub"><b>Recuperação solicitada.</b> Se houver uma conta ativa com este e-mail, você receberá em instantes uma mensagem com o link para definir uma nova senha. O link vale por 30 minutos e pode ser usado uma vez.</p>
            <p className="card-sub">Não chegou? Confira a caixa de spam ou peça ajuda ao administrador da sua organização.</p>
            <div className="form-actions"><Link className="cta" to="/login">Voltar ao acesso</Link></div>
          </div>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            <p className="card-sub">Informe o e-mail da sua conta. Enviaremos um link para você definir uma nova senha. Ninguém, nem a administração, recebe esse link ou vê a sua senha.</p>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="field"><label htmlFor="rec-email">E-mail corporativo</label>
              <input id="rec-email" type="email" autoComplete="username" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!error} aria-required="true" /></div>
            <div className="form-actions">
              <Link className="secondary btn-link" to="/login">Voltar</Link>
              <button className="cta" type="submit" disabled={busy}>{busy ? 'Enviando…' : 'Enviar link'}</button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

const tokenFromHash = () => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? '';

export function ResetPassword() {
  const navigate = useNavigate();
  const toast = useToast();
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O token sai da barra de endereço assim que é lido (não fica no histórico nem em capturas de tela).
  useEffect(() => { if (window.location.hash) window.history.replaceState(null, '', window.location.pathname); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 10) { setError('A senha deve ter pelo menos 10 caracteres.'); return; }
    if (password !== again) { setError('As senhas não conferem.'); return; }
    setBusy(true); setError(null);
    try {
      await accessRecoveryAdapter.confirm(token, password);
      toast('Senha redefinida. Entre com a nova senha.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'invalid_token'
        ? 'Link inválido, expirado ou já utilizado. Peça uma nova recuperação de acesso.' : friendlyError(err, 'Não foi possível redefinir a senha agora.'));
      setBusy(false);
    }
  };
  return (
    <main className="onboarding">
      <BrandLogo variant="public" />
      <section className="card source-form" aria-labelledby="reset-title">
        <h1 id="reset-title" className="card-title">Definir nova senha</h1>
        {!token ? (
          <>
            <div className="form-error" role="alert">Link incompleto ou inválido. Abra o link exatamente como recebido no e-mail ou peça uma nova recuperação.</div>
            <div className="form-actions"><Link className="secondary btn-link" to="/login">Voltar ao acesso</Link><Link className="cta" to="/recuperar-acesso">Pedir novo link</Link></div>
          </>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            <p className="card-sub">Escolha uma senha com pelo menos 10 caracteres. Depois da troca, as sessões abertas da sua conta são encerradas.</p>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="field"><label htmlFor="new-password">Nova senha</label>
              <input id="new-password" type="password" autoComplete="new-password" minLength={10} maxLength={512} value={password} onChange={(e) => setPassword(e.target.value)} aria-required="true" /></div>
            <div className="field"><label htmlFor="new-password-again">Repita a nova senha</label>
              <input id="new-password-again" type="password" autoComplete="new-password" minLength={10} maxLength={512} value={again} onChange={(e) => setAgain(e.target.value)} aria-required="true" /></div>
            <div className="form-actions">
              <Link className="secondary btn-link" to="/login">Cancelar</Link>
              <button className="cta" type="submit" disabled={busy}>{busy ? 'Salvando…' : 'Redefinir senha'}</button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
